import cn from 'classnames';
import React, { FC, ReactElement, ReactNode, useState } from 'react';
import { withTranslation } from 'react-i18next';

import {
  Button,
  ILibHorizontalPosition,
  Popover,
} from '@infologistics/frontend-libraries';

import { AllowedAction } from '@app/utils/types';

import { getMassiveActions } from '@utils/utils';

import {
  IMassiveActionDropdownItem,
  IMassiveActionsDropdownProps as IProps,
} from './types';
import { useAppSelector } from '@app/hooks/redux';
import { useParams } from 'react-router';
import { IBoxRole } from '@app/store/modules/boxes/types';

import styles from './MassiveActionsDropdown.module.css';

const MassiveActionsDropdown: FC<IProps> = ({ ...props }) => {
  const { t, actionGroups, classes, disabled = false } = props;
  const [openPopover, setOpenPopover] = useState(false);
  const boxes = useAppSelector((state) => state.boxes.boxes);
  const { boxOguid } = useParams();
  const role =
    boxes.find((val) => val.oguid === boxOguid)?.role ?? IBoxRole.VIEWER;

  const closePopover = (): void => {
    setOpenPopover(false);
    props.onPopoverClose?.();
  };

  const togglePopover = (): void => {
    if (openPopover) {
      closePopover();
    } else {
      setOpenPopover(true);
    }
  };

  const toggleClose = (isOpenFromPopover: boolean): void => {
    if (!isOpenFromPopover) {
      closePopover();
    }
  };

  const renderActions = (): ReactNode => {
    const { page } = props;
    const actions = actionGroups ?? getMassiveActions(t, disabled, role, page);

    return <div className='p-1'>{actions.map(renderActionsGroup)}</div>;
  };

  const renderActionsGroup = (
    groupData: IMassiveActionDropdownItem[],
    id: number
  ): ReactNode => {
    if (!groupData.length) return null;
    return (
      <div key={id} className={styles.group}>
        {groupData.map(renderActionButton)}
      </div>
    );
  };

  const renderActionButton = ({
    name,
    translation,
    isDisabled,
    expandedContent,
    keepOpen,
  }: IMassiveActionDropdownItem): ReactElement => (
    <div key={name}>
      <Button
        classes={cn('full-width', 'p-1', styles.button)}
        theme='text'
        onClick={handleButtonClick(name, Boolean(keepOpen))}
        isDisabled={isDisabled}
      >
        {translation}
      </Button>
      {expandedContent}
    </div>
  );

  const handleButtonClick = (name: string, keepOpen: boolean) => () => {
    const { onActionSelect } = props;

    if (!keepOpen) closePopover();
    onActionSelect((AllowedAction as Record<string, string>)[name] ?? name);
  };

  return (
    <Popover
      popoverClasses={classes}
      containerClasses={styles.container}
      buttonComponent={
        <Button
          onClick={togglePopover}
          rightIcon='IconAngle'
          rightIconProps={{
            size: 'xs',
            color: 'muted-500',
            rotation: openPopover ? '180' : '0',
            externalClass: 'mt-1',
          }}
        >
          {t('envelopes:table.head.actions')}
        </Button>
      }
      horizontalPosition={ILibHorizontalPosition.RIGHT}
      withoutArrow
      parentManagement={{
        isOpenFromParent: openPopover,
        callbackForClose: toggleClose,
      }}
    >
      {renderActions()}
    </Popover>
  );
};

export default withTranslation()(MassiveActionsDropdown);
